import { Id, toast } from 'react-toastify';
import translationFallback from './translations/translationFallback';
import {
  DismissToast,
  DynamicMessage,
  GetToastData,
  MessageInfo,
  ShowToast,
  ShowToastType,
  ToastMessagesKeys,
  TypedTranslationFunction,
} from '@/types/other';
import { toastMessages } from '@/constants/toastMessages';

const isDynamicMessage = (
  messageInfo: MessageInfo
): messageInfo is DynamicMessage => {
  return typeof messageInfo.default === 'function';
};

const showToast: ShowToast = (messageKey, type, t, dynamicValue) => {
  const messageInfo = toastMessages[`${messageKey}`];

  let message: string;
  if (isDynamicMessage(messageInfo) && dynamicValue) {
    message = messageInfo.default(dynamicValue);
  } else {
    message = messageInfo.default as string;
  }

  const translatedMessage = messageInfo.translatedKey;
  const finalTranslatedMessage = translationFallback(
    translatedMessage,
    t(translatedMessage),
    message
  );

  switch (type) {
    case 'loading':
      return toast.loading(finalTranslatedMessage);
    case 'success':
      return toast.success(finalTranslatedMessage);
    case 'error':
      return toast.error(finalTranslatedMessage);
    default:
      return toast(finalTranslatedMessage);
  }
};

function showTypedToast<T extends string = string>(
  messageKey: ToastMessagesKeys,
  type: ShowToastType,
  t: TypedTranslationFunction<T>,
  dynamicValue?: string
) {
  const messageInfo = toastMessages[`${messageKey}`];

  let message: string;
  if (isDynamicMessage(messageInfo) && dynamicValue) {
    message = messageInfo.default(dynamicValue);
  } else {
    message = messageInfo.default as string;
  }

  const tKey = messageInfo.translatedKey as T;

  const finalTranslatedMessage = t(tKey, message);

  switch (type) {
    case 'loading':
      return toast.loading(finalTranslatedMessage);
    case 'success':
      return toast.success(finalTranslatedMessage);
    case 'error':
      return toast.error(finalTranslatedMessage);
    default:
      return toast(finalTranslatedMessage);
  }
}

const dismissToast: DismissToast = async (toastId, seconds, showToastData) => {
  try {
    const waitTime = seconds ? (seconds >= 1.5 ? seconds * 1000 : 1500) : 1500;
    await new Promise((resolve) => setTimeout(resolve, waitTime));

    if (toastId) {
      toast.dismiss(toastId);
    }

    if (showToastData) {
      const { messageKey, type, t, dynamicValue } = showToastData;
      const toastId: Id = showToast(messageKey, type, t, dynamicValue);
      return toastId;
    }
  } catch (e) {
    console.error(e);
  }
};

const getToastData: GetToastData = (messageKey, type, t, dynamicValue) => {
  return { messageKey, type, t, dynamicValue };
};

export { showToast, showTypedToast, dismissToast, getToastData };
